import React, { useState, useEffect } from "react";
import SearchCard from "../components/SearchCard";
import ResultsCard from "../components/ResultsCard";
import StateSelector from "../components/StateSelector";
import CountySelector from "../components/CountySelector";
import { useNavigate, useLocation } from "react-router-dom";
import { useKeycloak } from "../config/KeycloakContext";
import { useUserContext } from "../config/UserContext";
import type { State, County } from "../../../shared/types/models/location";

const SearchResultsPage: React.FC = () => {
  const [showResults, setShowResults] = useState(false);
  const [reference, setReference] = useState("");
  const [reference2To, setReference2To] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { getToken } = useKeycloak();
  const { apiBaseUrl, apiBaseUrlLoading, apiBaseUrlError } = useUserContext();
  const { state, setState, county, setCounty, setFreeze, setDocument, setEntities } = useUserContext();

  useEffect(() => {
    // Reset results if user returns to this page
    setShowResults(false);
    // Clear all UserContext values on mount
    setState(null);
    setCounty(null);
    setFreeze(null);
    setDocument(null);
    setEntities([]);
  }, [location.pathname]);

  const performCreateFlow = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      if (!token) throw new Error("No token");

      const freezeRes = await fetch(`${apiBaseUrl}/api/frozendeed/freeze/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          reference: "NEW_FREEZE",
          stateId: state?.id,
          countyId: county?.id,
          recordingIndexes: {},
        }),
      });
      if (!freezeRes.ok) {
        const errBody = await freezeRes.json().catch(() => ({}));
        throw new Error(errBody.message || "Error #FZD-101: Error loading county template.");
      }
      const freezeData = await freezeRes.json();

      const docRes = await fetch(`${apiBaseUrl}/api/frozendeed/document/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          freezeLookup: freezeData.data.lookup,
          type: 1 // DOCUMENT_TYPE.LIEN
        }),
      });
      if (!docRes.ok) throw new Error("Failed to create document");
      const docData = await docRes.json();

      const docId = docData.data.lookup;
      // Navigate to /start — FlowPage will load the schema and auto-route
      navigate(`/document/${docId}/start`);
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message || "Failed to start create flow");
      } else {
        setError("Failed to start create flow");
      }
    } finally {
      setLoading(false);
    }
  };



  const handleCreateNew = () => {
    if (!state || !county) {
      setError("Please select a State and County first");
      return;
    }
    performCreateFlow();
  };

  const handleCreateLien = () => {
    performCreateFlow();
  };

  if (apiBaseUrlLoading) return <div>Loading configuration...</div>;
  if (apiBaseUrlError) return <div>Error loading configuration: {apiBaseUrlError}</div>;

  return (
    <div className="w-full max-w-full flex flex-col items-center pt-8 mx-auto bg-white">
      
      {/* State & County Selection (Top Level) */}
      <div className="bg-white rounded-2xl px-8 py-6 flex flex-col items-center max-w-md w-full shadow-sm border border-gray-100 mb-6">
        <h2 className="text-[20px] font-bold text-[#141435] mb-4 text-center font-plus-jakarta">
          Select Location
        </h2>
        <div className="w-full space-y-4">
          <StateSelector
            value={state}
            onChange={(val: State | null) => {
              setState(val);
              setCounty(null);
              setError(null);
            }}
            label="State"
            filter={{ enabled: true }}
            dropdownAnchor="bottom"
          />
          <CountySelector
            value={county}
            onChange={(val: County | null) => {
              setCounty(val);
              setError(null);
            }}
            state={state}
            label="County"
            dropdownAnchor="bottom"
          />
        </div>
      </div>

      <div className="w-full max-w-md border-t border-gray-200 my-2"></div>

      {/* Create New Document Button */}
      <div className="w-full max-w-md my-6">
        <button
          onClick={handleCreateNew}
          disabled={!state || !county}
          className={`w-full py-3 rounded-full font-bold text-[16px] font-plus-jakarta transition-all flex items-center justify-center gap-2 ${
            state && county 
              ? 'bg-[#0B4BB6] text-white hover:bg-[#23235a] shadow-md hover:shadow-lg' 
              : 'bg-gray-200 text-gray-500 cursor-not-allowed'
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"/>
          </svg>
          Create A New Frozen Document
        </button>
      </div>

      <div className="w-full max-w-md border-t border-gray-200 my-2 mb-8"></div>

      <SearchCard
        state={state}
        county={county}
        onSearch={async (payload: any) => {
          setError(null);
          if (payload.mode === 'property') {
            const { reference: ref, reference2To: ref2 } = payload;
            setReference(ref);
            setReference2To(ref2 || "");
            setShowResults(true);

            // --- API call to /api/frozendeed/search ---
            try {
              const token = await getToken();
              if (!token) return;
              const indexes = county?.documentSchema?.indexes || [];
              const searchParts = [];
              if (indexes.length > 0 && indexes[0].isUniqueKey !== false && ref) {
                  searchParts.push(ref.trim());
              } else if (indexes.length === 0 && ref) {
                  searchParts.push(ref.trim()); // Fallback if no schema
              }
              
              if (indexes.length > 1 && ref2 && indexes[1].isUniqueKey !== false) {
                  searchParts.push(ref2.trim());
              } else if (indexes.length === 0 && ref2) {
                  searchParts.push(ref2.trim()); // Fallback if no schema
              }
              
              const builtReference = searchParts.join(' ');

              const params = new URLSearchParams({
                stateId: state!.id.toString(),
                countyId: county!.id.toString(),
                reference: builtReference,
              });
              const response = await fetch(`${apiBaseUrl}/api/frozendeed/search?${params.toString()}`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              const data = await response.json();
              if (data && data.data && Array.isArray(data.data.items) && data.data.items.length > 0) {
                // Assume first freeze is the one to show
                const foundFreeze = data.data.items[0];
                setFreeze(foundFreeze);
                setDocument(data.data.document);
                // Log navigation and state
                console.log('[SearchPage] Navigating to /freeze/' + foundFreeze.lookup, { foundFreeze, document: data.data.document });
                // Success: navigate to new deep link pattern
                navigate(`/freeze/${foundFreeze.lookup}`);
              }
            } catch (err: unknown) {
              if (err instanceof Error) {
                setError(err.message || "Failed to search");
              } else {
                setError("Failed to search");
              }
            }
          } else if (payload.mode === 'deed') {
            setShowResults(false);
            try {
              const token = await getToken();
              if (!token) return;
              const res = await fetch(`${apiBaseUrl}/api/frozendeed/document/${payload.frozenDeedLookup}/with-freeze`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              const data = await res.json();
              if (!res.ok) {
                throw new Error(data?.error?.message || "Frozen Deed not found");
              }
              if (data?.data?.freeze) {
                setFreeze(data.data.freeze);
                navigate(`/freeze/${data.data.freeze.lookup}`);
              } else {
                setError("Associated property not found");
              }
            } catch (err: unknown) {
              if (err instanceof Error) {
                setError(err.message || "Failed to find Frozen Deed");
              } else {
                setError("Failed to find Frozen Deed");
              }
            }
          }
        }}
      />
      {error && (
        <div className="mt-4 bg-red-50 text-red-500 border border-red-200 px-4 py-3 rounded-lg max-w-md w-full text-center">
          {error}
        </div>
      )}
      {showResults && (
        <ResultsCard
          onCreateLien={handleCreateLien}
          loading={loading}
          reference={reference}
        />
      )}


    </div>
  );
};

export default SearchResultsPage;